<template>
  <div class="signup-container">
    <div class="container-fluid bg-white h-100">
      <div class="row h-100 no-gutters">
        <!-- Left Side - Form -->
        <div
          class="col-lg-6 d-flex align-items-center justify-content-center mb-5"
        >
          <div class="col-md-9">
            <!-- Logo -->
            <div class="row mb-4 mt-3">
              <div class="col-12">
                <div class="logo-container">
                  <img
                    src="../../assets/churchplusblueLogo.png"
                    alt="ChurchPlus"
                    class="logo"
                  />
                </div>
              </div>
            </div>

            <!-- Form Header -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="signup-title font-weight-600 w-100 s-30">
                  Sign up to ChurchPlus payment
                </div>
              </div>
            </div>
            <!-- Signup Form -->
            <div class="row">
              <div class="col-12 grey-backg border-radius-10 p-5">
                <el-form
                  ref="signupFormRef"
                  :model="signupForm"
                  :rules="formRules"
                  label-position="top"
                  class="signup-form"
                  @submit.prevent="handleSignup"
                >
                  <!-- Full Name -->
                  <div class="form-group mb-3">
                    <label class="form-label">Full Name</label>
                    <el-input
                      v-model="signupForm.fullName"
                      placeholder="full name"
                      class="custom-input"
                      :prefix-icon="User"
                    />
                  </div>

                  <!-- Phone Number -->
                  <div class="form-group mb-3">
                    <label class="form-label">Phone Number</label>
                    <el-input
                      v-model="signupForm.phoneNumber"
                      placeholder="Enter Phone Number"
                      class="custom-input"
                      :prefix-icon="Phone"
                    />
                  </div>

                  <!-- Email Address -->
                  <div class="form-group mb-3">
                    <label class="form-label">Email Address</label>
                    <el-input
                      v-model="signupForm.email"
                      type="email"
                      placeholder="Enter Your Email"
                      class="custom-input"
                      :prefix-icon="Message"
                    />
                  </div>

                  <!-- Password -->
                  <div class="form-group mb-4">
                    <label class="form-label">Password</label>
                    <el-input
                      v-model="signupForm.password"
                      type="password"
                      placeholder="••••••••••••"
                      class="custom-input"
                      :prefix-icon="Lock"
                      show-password
                    />
                  </div>

                  <!-- Sign Up Button -->
                  <div class="form-group mb-4">
                    <el-button
                      size="large"
                      class="signup-btn w-100"
                      :loading="isLoading"
                      @click="handleSignup"
                    >
                      {{ isLoading ? "Signing Up..." : "Sign Up" }}
                    </el-button>
                  </div>

                  <!-- Google Sign Up -->
                  <div class="form-group">
                    <el-button
                      size="large"
                      class="google-btn w-100"
                      @click="handleGoogleSignup"
                    >
                      <img
                        src="../../assets/small-google.png"
                        alt="Google"
                        class="google-icon me-2"
                      />
                      Sign on with Google
                    </el-button>
                  </div>

                  <!-- Sign In Link -->
                  <div class="form-group text-center">
                    <span class="signin-text">
                      Already have an account?
                      <router-link to="/signinpayment" class="signin-link"
                        >sign in</router-link
                      >
                    </span>
                  </div>

                  <!-- Go Back Link -->
                  <div class="form-group text-center mt-3">
                    <span class="goback-text text-dak s-14 fw-500">
                      Go back to
                      <span class="goback-lin cursor-pointer" @click="goBack"
                        ><u>Give</u></span
                      >
                    </span>
                  </div>
                </el-form>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Side - Illustration -->
        <div class="col-lg-6 d-none d-lg-block bg-wall"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { ElNotification } from "element-plus";
import { Lock, Message, Phone, User } from "@element-plus/icons-vue";
import axios from "@/gateway/backendapi";
import finish from "@/services/progressbar/progress";

// Router setup
const router = useRouter();
const route = useRoute();

// Form reference
const signupFormRef = ref();

// Reactive data
const isLoading = ref(false);
const tenantId = ref("");

const signupForm = reactive({
  fullName: "",
  phoneNumber: "",
  email: "",
  password: "",
});

// Form validation rules
const formRules = reactive({
  fullName: [
    { required: true, message: "Please enter your full name", trigger: "blur" },
    {
      min: 2,
      max: 50,
      message: "Name should be 2 to 50 characters",
      trigger: "blur",
    },
  ],
  phoneNumber: [
    {
      required: true,
      message: "Please enter your phone number",
      trigger: "blur",
    },
    {
      pattern: /^[0-9+\-\s()]+$/,
      message: "Please enter a valid phone number",
      trigger: "blur",
    },
  ],
  email: [
    { required: true, message: "Please enter your email", trigger: "blur" },
    {
      type: "email",
      message: "Please enter a valid email address",
      trigger: "blur",
    },
  ],
  password: [
    { required: true, message: "Please enter your password", trigger: "blur" },
    {
      min: 6,
      message: "Password should be at least 6 characters",
      trigger: "blur",
    },
  ],
});

// Methods
const getTenantId = async () => {
  try {
    const response = await axios.get(
      `/give?paymentFormID=${route.params.userId}`
    );
    tenantId.value = response.data.tenantID;
    console.log("Tenant ID retrieved:", tenantId.value);
  } catch (error) {
    console.error("Error getting tenant ID:", error);
    ElNotification({
      title: "Error",
      message: "Failed to load payment form details",
      type: "error",
      duration: 3000,
    });
    finish();
  }
};

const handleSignup = async () => {
  if (!signupFormRef.value) return;

  try {
    const valid = await signupFormRef.value.validate();
    if (!valid) return;

    isLoading.value = true;

    const signupDetails = {
      name: signupForm.fullName,
      phoneNumber: signupForm.phoneNumber,
      tenantId: tenantId.value,
      email: signupForm.email,
      password: signupForm.password,
    };

    const { data } = await axios.post(
      "/mobile/v1/Account/WebSignUp",
      signupDetails
    );

    if (!data.status) {
      ElNotification({
        title: "Signup Failed",
        message: data.response || "Something went wrong during signup",
        type: "warning",
        duration: 4000,
      });
    } else if (data && data.returnObject.token && data.status) {
      ElNotification({
        title: "Success!",
        message: data.response || "Account created successfully",
        type: "success",
        duration: 3000,
      });

      const giverDetails = {
        giverToken: data.returnObject.token,
        giverId: data.returnObject.userId,
        email: data.returnObject.email,
        name: data.returnObject.fullname,
        phone: data.returnObject.phoneNumber,
        setSignInStatus: true,
      };

      localStorage.setItem("giverToken", JSON.stringify(giverDetails));
      // router.push(`/onlinedonorinfo/${data.returnObject.personID}`);

      // router.push({
      //   name: "OnlineGiving4",
      //   params: { userId: route.params.userId },
      // });
      router.push({
        name: "NewGivingForm",
        params: { userId: route.params.userId },
      });
    }
  } catch (error) {
    console.error("Signup error:", error);

    if (error.response) {
      ElNotification({
        title: "Signup Error",
        message: error.response.data || "Failed to create account",
        type: "error",
        duration: 4000,
      });
    } else if (error.message.toLowerCase().includes("network")) {
      ElNotification({
        title: "Network Error",
        message: "Please ensure you have a strong internet connection",
        type: "error",
        duration: 4000,
      });
    } else if (error.message.toLowerCase().includes("timeout")) {
      ElNotification({
        title: "Request Timeout",
        message: "Request took too long to respond. Please refresh the page",
        type: "error",
        duration: 4000,
      });
    } else {
      ElNotification({
        title: "Error",
        message: "An unexpected error occurred. Please try again",
        type: "error",
        duration: 4000,
      });
    }
  } finally {
    isLoading.value = false;
    finish();
  }
};

const handleGoogleSignup = () => {
  ElNotification({
    title: "Coming Soon",
    message: "Google signup will be available soon",
    type: "info",
    duration: 3000,
  });
};

const goBack = () => {
  // router.go(-2);
  router.push(`/give/${route.params.userId}`);
};

// Lifecycle
onMounted(() => {
  getTenantId();
});
</script>

<style scoped>
.grey-backg {
  box-shadow: 0px 4px 10px 0px #0000001a;
}
.bg-wall {
  background-image: url("../../assets/posimage.png");
  background-size: 100%;
  background-position: center;
  /* height: 100%; */
  background-repeat: no-repeat;
}
.signup-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.signup-form-container {
  max-width: 550px;
  width: 100%;
  padding: 2rem;
}

.logo-container {
  margin-bottom: 1rem;
}

.logo {
  height: 40px;
  width: auto;
}

.signup-title {
  background: linear-gradient(270deg, #0e59e4 0%, #043591 55.29%);
  background-clip: text;
  color: transparent;
}

.form-label {
  font-size: 14px;
  font-weight: 400;
  color: #000000;
  margin-bottom: 0.5rem;
  display: block;
}

.custom-input {
  margin-bottom: 0;
}

:deep(.el-input__wrapper) {
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: none;
  transition: all 0.2s ease;
}

:deep(.el-input__wrapper:hover) {
  border-color: #cbd5e0;
}

:deep(.el-input__wrapper.is-focus) {
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
}

:deep(.el-input__inner) {
  color: #2d3748;
  font-size: 0.875rem;
}

:deep(.el-input__inner::placeholder) {
  color: #a0aec0;
}

.signup-btn {
  background: #0b55d4 !important;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  border: none;
  border-radius: 60px;
  padding: 14px 24px;
  font-size: 1rem;
  font-weight: 600;
  height: auto;
  color: #ffffff !important;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
}

.signup-btn:hover {
  background: #0b55d4 !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
  color: #ffffff !important;
}

.google-btn {
  background: #475467;
  border: 2px solid #e2e8f0;
  border-radius: 60px;
  padding: 14px 24px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  height: auto;
  transition: all 0.2s ease;
}

.google-btn:hover {
  border-color: #cbd5e0;
  background: #3e4857;
}

.google-icon {
  width: 20px;
  height: 20px;
  margin-right: 8px;
}

.signin-text {
  font-size: 14px;
  color: #000000;
}

.signin-link {
  color: #000000;
  text-decoration: none;
  text-decoration: underline;
  font-weight: 500;
}

.signin-link:hover {
  color: #2c5282;
  text-decoration: underline;
}

.goback-text {
  font-size: 0.875rem;
  color: #718096;
}

.goback-link {
  color: #3182ce;
  cursor: pointer;
  font-weight: 600;
}

.goback-link:hover {
  color: #2c5282;
  text-decoration: underline;
}

/* Responsive Design */
@media (max-width: 991.98px) {
  .signup-form-container {
    padding: 1.5rem;
  }

  .signup-title {
    font-size: 1.75rem;
  }
}

@media (max-width: 575.98px) {
  .signup-form-container {
    padding: 1rem;
  }

  .signup-title {
    font-size: 1.5rem;
  }
}

/* Bootstrap utilities */
.w-100 {
  width: 100% !important;
}

.text-center {
  text-align: center !important;
}

.me-2 {
  margin-right: 0.5rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

.mb-4 {
  margin-bottom: 1.5rem !important;
}

.mt-3 {
  margin-top: 1rem !important;
}

.h-100 {
  height: 100% !important;
}

.no-gutters {
  margin-right: 0;
  margin-left: 0;
}

.no-gutters > .col,
.no-gutters > [class*="col-"] {
  padding-right: 0;
  padding-left: 0;
}
</style>
